/**
 * DriverAppManagerPage · v2.7.65+E129 (2026-02-XX)
 * ═════════════════════════════════════════════════════════════════════
 * Zone Editor — L5 admins (super_user + general_partner) draw new
 * dispatch/turbo zones on a Leaflet + OpenStreetMap canvas and publish
 * them to drivers.
 *
 *   Zero external API keys (OSM tiles are free).
 *   Zero Google Maps SDK (matches Driver Console credit-burn policy).
 *   Supports CIRCLES + POLYGONS via leaflet-draw.
 *
 * Draft → Publish workflow:
 *   • New shapes start as DRAFT (amber).
 *   • Publish button flips to PUBLISHED (emerald) and merges into
 *     /api/driver/wait-times on the next 45s driver poll.
 *   • Unpublish reverts to DRAFT (still visible to admins, invisible to drivers).
 *
 * RBAC gate: `_load()` first attempts GET /admin/driver-app-manager/zones
 * — a 403 flips the shell to a "denied" screen mirroring MasterAccessPage.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, Loader2, Lock, Save, Trash2, MapPinned,
  CheckCircle2, XCircle, Circle as CircleIcon, Pentagon,
  Plus, RefreshCw, EyeOff, Download, Upload, ShieldCheck,
  Users, Clock, Flame, Bookmark, Zap,
} from "lucide-react";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";

// Fix Leaflet default icon paths (bundler workaround)
import L from "leaflet";
import iconRetina from "leaflet/dist/images/marker-icon-2x.png";
import iconUrl from "leaflet/dist/images/marker-icon.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl: iconRetina, iconUrl, shadowUrl });

import "leaflet-draw";

/* ─────────────────────────── constants ──────────────────────────── */
// Weatherford, OK default center — matches the seeded ZONES list
const DEFAULT_CENTER = [35.5262, -98.7075];
const DEFAULT_ZOOM = 10;
const OSM_TILE = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const OSM_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

const STATUS_STYLES = {
  draft:     { color: "#F59E0B", fill: "#F59E0B22", label: "DRAFT" },
  published: { color: "#10B981", fill: "#10B98122", label: "PUBLISHED" },
};

const HEAT_STYLES = {
  hot:   { color: "#EF4444", fill: "#EF444422" },
  warm:  { color: "#F59E0B", fill: "#F59E0B22" },
  quiet: { color: "#3B82F6", fill: "#3B82F622" },
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
}

/* ─────────────────────────── Presets bar ────────────────────────── */
function PresetsBar({ onPresetActivated }) {
  const [presets, setPresets] = useState([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveDesc, setSaveDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const [activatingId, setActivatingId] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/driver-app-manager/presets");
      setPresets(data?.presets || []);
    } catch (_e) { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const activate = async (id, name) => {
    if (!window.confirm(`Activate preset "${name}"?\n\nThis will publish every zone in the preset AND unpublish every zone that is currently published but NOT in the preset.`)) return;
    setActivatingId(id);
    try {
      const { data } = await adminApi.post(`/admin/driver-app-manager/presets/${id}/activate`);
      toast.success(`Preset activated · +${data.published} published · -${data.unpublished} unpublished` + (data.skipped ? ` · ${data.skipped} skipped` : ""));
      onPresetActivated?.();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Activate failed");
    } finally { setActivatingId(""); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Delete preset "${name}"? This does not affect any published zones.`)) return;
    try {
      await adminApi.delete(`/admin/driver-app-manager/presets/${id}`);
      toast.success("Preset deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const save = async () => {
    if (!saveName || saveName.trim().length < 2) {
      toast.error("Preset name must be at least 2 characters");
      return;
    }
    setSaving(true);
    try {
      await adminApi.post("/admin/driver-app-manager/presets", {
        name: saveName.trim(),
        description: saveDesc.trim() || undefined,
      });
      toast.success(`Preset "${saveName}" saved`);
      setShowSaveDialog(false);
      setSaveName("");
      setSaveDesc("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="rounded-xl border border-amber-500/25 bg-black/40 p-3 flex flex-wrap items-center gap-2"
      data-testid="presets-bar"
    >
      <div className="flex items-center gap-1.5 pr-2 border-r border-amber-500/30">
        <Bookmark className="h-4 w-4 text-amber-400" />
        <span className="text-xs uppercase tracking-wider font-bold text-amber-300">Presets</span>
      </div>

      {presets.length === 0 && (
        <span className="text-xs text-zinc-500 italic" data-testid="presets-empty">
          No saved presets yet. Publish some zones then save this arrangement as a preset.
        </span>
      )}

      {presets.map((p) => (
        <div
          key={p.id}
          data-testid={`preset-chip-${p.id}`}
          className="flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-xs text-amber-200"
        >
          <button
            onClick={() => activate(p.id, p.name)}
            disabled={activatingId === p.id}
            data-testid={`preset-activate-${p.id}`}
            className="font-bold hover:text-amber-100 disabled:opacity-50"
            title={p.description || `Activate "${p.name}"`}
          >
            {activatingId === p.id
              ? <Loader2 className="h-3 w-3 inline animate-spin mr-1" />
              : <Zap className="h-3 w-3 inline mr-1" />}
            {p.name}
          </button>
          <span className="text-zinc-500">·</span>
          <span className="text-zinc-400 text-[10px]">{(p.entries || []).length} zones</span>
          <button
            onClick={() => del(p.id, p.name)}
            data-testid={`preset-delete-${p.id}`}
            className="ml-1 text-rose-400 hover:text-rose-200"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      ))}

      <Button
        size="sm"
        onClick={() => setShowSaveDialog(true)}
        data-testid="preset-save-open"
        className="h-7 text-xs bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/40"
      >
        <Plus className="h-3 w-3 mr-1" />Save current as preset
      </Button>

      {showSaveDialog && (
        <div
          className="absolute z-[9999] mt-14 rounded-lg border border-amber-500 bg-[#212529] p-4 shadow-xl w-96"
          data-testid="preset-save-dialog"
          style={{ left: "50%", transform: "translateX(-50%)" }}
        >
          <h3 className="text-sm font-bold text-amber-300 uppercase mb-2">Save Preset</h3>
          <div className="space-y-2">
            <Input
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              placeholder='e.g. "Race Day", "Weekend Nights"'
              data-testid="preset-save-name"
              className="bg-black/40 border-amber-500/40 text-white"
              autoFocus
            />
            <Input
              value={saveDesc}
              onChange={(e) => setSaveDesc(e.target.value)}
              placeholder="Optional description"
              data-testid="preset-save-desc"
              className="bg-black/40 border-amber-500/40 text-white"
            />
            <div className="flex gap-2">
              <Button
                onClick={save}
                disabled={saving}
                data-testid="preset-save-confirm"
                className="flex-1 bg-amber-500 hover:bg-amber-400 text-black font-bold"
              >
                <Save className="h-4 w-4 mr-1" />Save Preset
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowSaveDialog(false)}
                data-testid="preset-save-cancel"
                className="border-rose-500 text-rose-300"
              >
                <XCircle className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-[10px] text-zinc-500">
              Snapshots the currently-published zones + their heat hints.
              Activate later to swap the published set back to this arrangement.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── Heatmap snapshot ───────────────────── */
function HeatmapPanel() {
  const [groupBy, setGroupBy] = useState("day_of_week");
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [capturing, setCapturing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get(
        `/admin/driver-app-manager/heatmap/rollup?days=${days}&group_by=${groupBy}`
      );
      setData(data);
    } catch (_e) { setData(null); }
    finally { setLoading(false); }
  }, [groupBy, days]);
  useEffect(() => { load(); }, [load]);

  const captureNow = async () => {
    setCapturing(true);
    try {
      const { data } = await adminApi.post("/admin/driver-app-manager/heatmap/capture-now");
      toast.success(`Snapshot captured · ${data.written} rows`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Capture failed");
    } finally { setCapturing(false); }
  };

  const buckets = data?.n_buckets || 0;
  const zonesList = data?.zones || [];
  const cellIndex = useMemo(() => {
    const idx = {};
    for (const c of (data?.cells || [])) {
      idx[`${c.bucket}::${c.zone_key}`] = c;
    }
    return idx;
  }, [data]);

  return (
    <div
      className="rounded-xl border border-amber-500/25 bg-[#212529] p-4"
      data-testid="heatmap-panel"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className="rounded-lg p-2 border border-amber-500 bg-amber-500/10">
            <Flame className="h-5 w-5 text-amber-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-amber-300 uppercase tracking-wide">Zone Heatmap Snapshot</h2>
            <p className="text-xs text-zinc-400">Hourly snapshots · compare demand across days &amp; hours</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
            data-testid="heatmap-groupby"
            className="bg-black/40 border border-amber-500/40 rounded-md px-3 py-1.5 text-sm text-white"
          >
            <option value="day_of_week">Day of Week</option>
            <option value="hour_of_day">Hour of Day (UTC)</option>
          </select>
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            data-testid="heatmap-days"
            className="bg-black/40 border border-amber-500/40 rounded-md px-3 py-1.5 text-sm text-white"
          >
            <option value={1}>Last 24h</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Button
            size="sm"
            onClick={captureNow}
            disabled={capturing}
            data-testid="heatmap-capture-now"
            className="bg-amber-500 hover:bg-amber-400 text-black font-bold"
          >
            {capturing
              ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              : <RefreshCw className="h-3 w-3 mr-1" />}
            Capture Now
          </Button>
        </div>
      </div>

      {loading && <div className="text-sm text-zinc-400" data-testid="heatmap-loading">Loading…</div>}
      {!loading && zonesList.length === 0 && (
        <div className="text-sm text-zinc-500 py-8 text-center" data-testid="heatmap-empty">
          No snapshots yet. Click <span className="text-amber-300 font-semibold">Capture Now</span> to
          seed the first rollup — the hourly scheduler will take over from there.
        </div>
      )}
      {!loading && zonesList.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs" data-testid="heatmap-grid">
            <thead>
              <tr>
                <th className="text-left py-2 px-2 text-zinc-400 uppercase tracking-wider">Zone</th>
                {Array.from({ length: buckets }, (_, i) => (
                  <th key={i} className="py-2 px-1 text-zinc-400 uppercase text-[10px]">
                    {groupBy === "day_of_week"
                      ? ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][i]
                      : String(i).padStart(2, "0")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {zonesList.map((z) => (
                <tr key={z.key} data-testid={`heatmap-row-${z.key}`}>
                  <td className="py-1.5 px-2 text-zinc-200 truncate max-w-[180px]">{z.label}</td>
                  {Array.from({ length: buckets }, (_, i) => {
                    const cell = cellIndex[`${i}::${z.key}`];
                    if (!cell) {
                      return (
                        <td key={i} className="py-1.5 px-1 text-center text-zinc-700">·</td>
                      );
                    }
                    const style = HEAT_STYLES[cell.heat];
                    return (
                      <td
                        key={i}
                        title={`Bucket ${cell.bucket_label} · ${cell.samples} samples · peak ${cell.peak_riders}r/${cell.peak_drivers}d · avg ${cell.avg_riders}r/${cell.avg_drivers}d · ${cell.heat}`}
                        className="py-1.5 px-1 text-center font-bold text-[10px] cursor-help"
                        style={{ backgroundColor: style.fill, color: style.color }}
                        data-testid={`heatmap-cell-${z.key}-${i}`}
                      >
                        {cell.avg_riders > 0 ? cell.avg_riders.toFixed(1) : "0"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-[11px] text-zinc-500 mt-3 flex items-center gap-4 flex-wrap">
            <span>Cells show average riders waiting during that bucket</span>
            <span>·</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: HEAT_STYLES.hot.fill, border: `1px solid ${HEAT_STYLES.hot.color}` }} />Hot</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: HEAT_STYLES.warm.fill, border: `1px solid ${HEAT_STYLES.warm.color}` }} />Warm</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: HEAT_STYLES.quiet.fill, border: `1px solid ${HEAT_STYLES.quiet.color}` }} />Quiet</span>
            <span>·</span>
            <span>Hover a cell for peaks &amp; sample count</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── Map canvas ────────────────────────── */
function ZoneMap({ zones, analyticsByKey, selectedId, onSelect, onCreated, onEdited, mapRef, drawApiRef }) {
  const containerRef = useRef(null);
  const layersRef = useRef({});             // zone_id → leaflet layer
  const drawnRef = useRef(null);            // FeatureGroup for user-drawn shapes
  const activeHandlerRef = useRef(null);    // Currently-enabled L.Draw handler (for cancel)

  // Init map once — guarded against React StrictMode double-invoke by
  // checking if the container already carries a Leaflet id.
  useEffect(() => {
    if (!containerRef.current) return;
    if (mapRef.current || containerRef.current._leaflet_id) return;
    const map = L.map(containerRef.current, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      scrollWheelZoom: true,
    });
    L.tileLayer(OSM_TILE, { attribution: OSM_ATTR, maxZoom: 19 }).addTo(map);

    const drawn = new L.FeatureGroup();
    map.addLayer(drawn);
    drawnRef.current = drawn;

    // v2.7.65+E131 · Register imperative draw handles so the parent's big
    // labeled buttons ("+ Draw Circle" / "+ Draw Polygon") can trigger the
    // draw handlers directly — much clearer UX than the tiny toolbar icons.
    const circleOpts = {
      shapeOptions: { color: "#FFD200", weight: 4, fillColor: "#FFD200", fillOpacity: 0.30 },
    };
    const polygonOpts = {
      allowIntersection: false,
      showArea: false,
      shapeOptions:  { color: "#FFD200", weight: 4, fillColor: "#FFD200", fillOpacity: 0.30 },
      guidelineDistance: 15,
      icon: new L.DivIcon({
        iconSize:  [14, 14],
        className: "bt-draw-vertex-marker",
      }),
      touchIcon: new L.DivIcon({
        iconSize:  [22, 22],
        className: "bt-draw-vertex-marker",
      }),
    };
    if (drawApiRef) {
      drawApiRef.current = {
        startCircle:  () => {
          if (activeHandlerRef.current?.disable) activeHandlerRef.current.disable();
          const h = new L.Draw.Circle(map, circleOpts);
          h.enable();
          activeHandlerRef.current = h;
        },
        startPolygon: () => {
          if (activeHandlerRef.current?.disable) activeHandlerRef.current.disable();
          const h = new L.Draw.Polygon(map, polygonOpts);
          h.enable();
          activeHandlerRef.current = h;
        },
        cancel:       () => {
          if (activeHandlerRef.current?.disable) activeHandlerRef.current.disable();
          activeHandlerRef.current = null;
        },
      };
    }

    map.on("draw:created", (e) => {
      const layer = e.layer;
      drawn.addLayer(layer);
      activeHandlerRef.current = null;
      let payload;
      if (e.layerType === "circle") {
        const c = layer.getLatLng();
        const meters = layer.getRadius();
        payload = {
          shape:     "circle",
          center:    { lat: c.lat, lng: c.lng },
          radius_mi: Number((meters / 1609.34).toFixed(3)),
        };
      } else if (e.layerType === "polygon") {
        const verts = layer.getLatLngs()[0].map((ll) => [ll.lat, ll.lng]);
        const centerLat = verts.reduce((s, [la]) => s + la, 0) / verts.length;
        const centerLng = verts.reduce((s, [, lo]) => s + lo, 0) / verts.length;
        payload = {
          shape:  "polygon",
          center: { lat: centerLat, lng: centerLng },
          polygon_coords: verts,
        };
      }
      if (payload) onCreated(payload);
      setTimeout(() => drawn.removeLayer(layer), 100);
    });

    map.on("draw:drawstop", () => { activeHandlerRef.current = null; });

    map.on("draw:edited", (e) => {
      e.layers.eachLayer((layer) => {
        const zoneId = layer.options?._zoneId;
        if (!zoneId) return;
        if (layer instanceof L.Circle) {
          const c = layer.getLatLng();
          onEdited(zoneId, {
            shape:     "circle",
            center:    { lat: c.lat, lng: c.lng },
            radius_mi: Number((layer.getRadius() / 1609.34).toFixed(3)),
          });
        } else if (layer instanceof L.Polygon) {
          const verts = layer.getLatLngs()[0].map((ll) => [ll.lat, ll.lng]);
          const cl = verts.reduce((s, [la]) => s + la, 0) / verts.length;
          const cn = verts.reduce((s, [, lo]) => s + lo, 0) / verts.length;
          onEdited(zoneId, {
            shape:  "polygon",
            center: { lat: cl, lng: cn },
            polygon_coords: verts,
          });
        }
      });
    });

    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      if (drawApiRef) drawApiRef.current = null;
    };
  }, []);

  // Repaint zones whenever the list changes
  useEffect(() => {
    const map = mapRef.current;
    const drawn = drawnRef.current;
    if (!map || !drawn) return;

    // Clear existing painted zones (keeps the FeatureGroup itself alive)
    Object.values(layersRef.current).forEach((l) => {
      try { drawn.removeLayer(l); } catch { /* ignore */ }
    });
    layersRef.current = {};

    zones.forEach((z) => {
      const statusStyle = STATUS_STYLES[z.status] || STATUS_STYLES.draft;
      // v2.7.65+E130 · Heat overlay — when analytics carries a heat value
      // for the zone, tint the fill with the heat color so admins can eye
      // demand at a glance. Falls back to status color when no analytics.
      const heatKey = analyticsByKey?.[z.id]?.heat;
      const heatStyle = heatKey ? HEAT_STYLES[heatKey] : null;
      const isSelected = z.id === selectedId;
      const paintOpts = {
        color:       statusStyle.color,
        fillColor:   heatStyle?.fill || statusStyle.fill,
        fillOpacity: heatStyle ? 0.45 : (isSelected ? 0.35 : 0.20),
        weight:      isSelected ? 4 : 2,
        _zoneId:     z.id,
      };
      let layer;
      if (z.shape === "circle" && z.center && z.radius_mi) {
        layer = L.circle([z.center.lat, z.center.lng], {
          ...paintOpts,
          radius: z.radius_mi * 1609.34,
        });
      } else if (z.shape === "polygon" && z.polygon_coords?.length >= 3) {
        layer = L.polygon(z.polygon_coords, paintOpts);
      }
      if (!layer) return;
      layer.on("click", () => onSelect(z.id));
      const a = analyticsByKey?.[z.id];
      const analyticsHtml = a
        ? `<br/><span style="color:${HEAT_STYLES[a.heat].color}">${a.heat.toUpperCase()}</span> · ${a.riders_waiting} riders · ${a.drivers_nearby} drivers · ~${a.median_wait_min}m wait`
        : "";
      layer.bindTooltip(
        `<strong>${z.label}</strong><br/>${statusStyle.label} · ${z.shape}${analyticsHtml}`,
        { direction: "top", sticky: true },
      );
      drawn.addLayer(layer);
      layersRef.current[z.id] = layer;
    });
  }, [zones, analyticsByKey, selectedId, onSelect]);

  // Fit-to-selection when the user picks a zone from the list
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedId) return;
    const layer = layersRef.current[selectedId];
    if (!layer) return;
    try {
      if (layer.getBounds) map.flyToBounds(layer.getBounds(), { padding: [40, 40], duration: 0.6 });
      else if (layer.getLatLng) map.flyTo(layer.getLatLng(), Math.max(map.getZoom(), 12), { duration: 0.6 });
    } catch { /* ignore */ }
  }, [selectedId]);

  return (
    <div
      ref={containerRef}
      data-testid="zone-editor-map"
      className="w-full h-[600px] rounded-xl overflow-hidden border border-amber-500/30"
      style={{ backgroundColor: "#0c0d10" }}
    />
  );
}

/* ─────────────────────────── Sub-panel · zone list ──────────────── */
function ZoneList({ zones, analyticsByKey, selectedId, onSelect, onDelete, onPublish, onUnpublish, onRefresh, loading }) {
  return (
    <div className="rounded-xl border border-amber-500/25 bg-[#212529] p-4" data-testid="zone-editor-list">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-bold tracking-wider uppercase text-amber-300 flex items-center gap-2">
          <MapPinned className="h-4 w-4" />Zones ({zones.length})
        </h2>
        <Button
          variant="ghost" size="sm"
          data-testid="zone-list-refresh"
          onClick={onRefresh}
          disabled={loading}
          className="h-7 text-zinc-300 hover:text-white"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>
      {zones.length === 0 && (
        <div className="text-sm text-zinc-500 py-6 text-center" data-testid="zone-list-empty">
          No custom zones yet. Draw one on the map to get started.
        </div>
      )}
      <ul className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
        {zones.map((z) => {
          const isSelected = z.id === selectedId;
          const style = STATUS_STYLES[z.status] || STATUS_STYLES.draft;
          return (
            <li key={z.id}>
              <button
                data-testid={`zone-row-${z.id}`}
                onClick={() => onSelect(z.id)}
                className={`w-full text-left rounded-lg px-3 py-2 border transition ${
                  isSelected
                    ? "border-amber-400 bg-amber-500/10"
                    : "border-white/10 hover:border-amber-500/40 hover:bg-white/5"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {z.shape === "polygon"
                      ? <Pentagon className="h-3.5 w-3.5 text-amber-400 flex-shrink-0" />
                      : <CircleIcon className="h-3.5 w-3.5 text-amber-400 flex-shrink-0" />}
                    <span className="font-medium text-sm text-zinc-100 truncate">{z.label}</span>
                  </div>
                  <span
                    className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full border whitespace-nowrap"
                    style={{ color: style.color, borderColor: style.color, backgroundColor: style.fill }}
                  >{style.label}</span>
                </div>
                <div className="mt-1 text-[11px] text-zinc-400 flex items-center gap-2 flex-wrap">
                  <span>{z.shape}</span>
                  <span>·</span>
                  <span>{z.shape === "circle" ? `${(z.radius_mi || 0).toFixed(2)} mi` : `${z.polygon_coords?.length || 0} vertices`}</span>
                  <span>·</span>
                  <span className="uppercase" style={{ color: HEAT_STYLES[z.heat_hint || "warm"].color }}>hint:{z.heat_hint || "warm"}</span>
                </div>
                {/* v2.7.65+E130 · Analytics overlay row */}
                {analyticsByKey?.[z.id] && (
                  <div
                    className="mt-1.5 flex items-center gap-2 text-[10px] font-mono flex-wrap"
                    data-testid={`zone-row-analytics-${z.id}`}
                  >
                    <span
                      className="px-1.5 py-0.5 rounded font-bold uppercase tracking-wider"
                      style={{
                        color: HEAT_STYLES[analyticsByKey[z.id].heat].color,
                        backgroundColor: HEAT_STYLES[analyticsByKey[z.id].heat].fill,
                      }}
                    >
                      <Flame className="h-2.5 w-2.5 inline mr-0.5" />{analyticsByKey[z.id].heat}
                    </span>
                    <span className="text-emerald-300 flex items-center gap-0.5">
                      <Users className="h-3 w-3" />{analyticsByKey[z.id].riders_waiting}
                    </span>
                    <span className="text-sky-300 flex items-center gap-0.5">
                      <CircleIcon className="h-3 w-3" />{analyticsByKey[z.id].drivers_nearby}d
                    </span>
                    <span className="text-amber-300 flex items-center gap-0.5">
                      <Clock className="h-3 w-3" />~{analyticsByKey[z.id].median_wait_min}m
                    </span>
                  </div>
                )}
              </button>
              {isSelected && (
                <div className="flex gap-1 mt-2 mb-1">
                  {z.status === "draft" ? (
                    <Button
                      size="sm"
                      data-testid={`zone-publish-${z.id}`}
                      onClick={() => onPublish(z.id)}
                      className="flex-1 h-8 text-xs bg-emerald-600 hover:bg-emerald-500 text-white"
                    >
                      <CheckCircle2 className="h-3 w-3 mr-1" />Publish
                    </Button>
                  ) : (
                    <Button
                      size="sm" variant="outline"
                      data-testid={`zone-unpublish-${z.id}`}
                      onClick={() => onUnpublish(z.id)}
                      className="flex-1 h-8 text-xs border-amber-500 text-amber-300"
                    >
                      <EyeOff className="h-3 w-3 mr-1" />Unpublish
                    </Button>
                  )}
                  <Button
                    size="sm" variant="outline"
                    data-testid={`zone-delete-${z.id}`}
                    onClick={() => {
                      if (window.confirm(`Delete zone "${z.label}"?\n\nThis cannot be undone.`)) onDelete(z.id);
                    }}
                    className="h-8 px-2 border-rose-500 text-rose-300 hover:bg-rose-500/10"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ─────────────────────────── Sub-panel · detail form ────────────── */
function ZoneDetailPanel({ selected, draftPayload, onDiscardDraft, onSaveDraft, onSaveEdit }) {
  const [label, setLabel] = useState(selected?.label || "");
  const [heatHint, setHeatHint] = useState(selected?.heat_hint || "warm");
  const [radius, setRadius] = useState(String(selected?.radius_mi || ""));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setLabel(selected?.label || "");
    setHeatHint(selected?.heat_hint || "warm");
    setRadius(String(selected?.radius_mi || ""));
  }, [selected?.id]);

  // Brand new draft (not yet saved) — collect label/heat then save
  if (draftPayload && !selected) {
    return (
      <div
        className="rounded-xl border border-amber-500 bg-amber-500/10 p-4"
        data-testid="zone-editor-newdraft"
      >
        <h2 className="text-sm font-bold uppercase tracking-wider text-amber-300 mb-3">
          New {draftPayload.shape} zone
        </h2>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-zinc-400 mb-1">Zone label</label>
            <Input
              data-testid="zone-new-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Weatherford Airport Loop"
              className="bg-black/40 border-amber-500/40 text-white"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-400 mb-1">Heat hint</label>
            <select
              data-testid="zone-new-heat"
              value={heatHint}
              onChange={(e) => setHeatHint(e.target.value)}
              className="w-full bg-black/40 border border-amber-500/40 rounded-md px-3 py-1.5 text-sm text-white"
            >
              <option value="hot">Hot (peak demand)</option>
              <option value="warm">Warm</option>
              <option value="quiet">Quiet</option>
            </select>
          </div>
          <div className="text-xs text-zinc-500 border border-white/10 rounded-md p-2 font-mono">
            <div>shape: {draftPayload.shape}</div>
            <div>center: {draftPayload.center.lat.toFixed(5)}, {draftPayload.center.lng.toFixed(5)}</div>
            {draftPayload.shape === "circle" && (
              <div>radius_mi: {draftPayload.radius_mi.toFixed(3)}</div>
            )}
            {draftPayload.shape === "polygon" && (
              <div>vertices: {draftPayload.polygon_coords.length}</div>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              onClick={async () => {
                if (!label || label.trim().length < 2) {
                  toast.error("Label must be at least 2 characters");
                  return;
                }
                setBusy(true);
                try {
                  await onSaveDraft({ ...draftPayload, label: label.trim(), heat_hint: heatHint });
                } finally { setBusy(false); }
              }}
              disabled={busy}
              data-testid="zone-new-save"
              className="flex-1 bg-amber-500 hover:bg-amber-400 text-black font-bold"
            >
              <Save className="h-4 w-4 mr-2" />Save Draft
            </Button>
            <Button
              variant="outline"
              onClick={onDiscardDraft}
              data-testid="zone-new-discard"
              className="border-rose-500 text-rose-300 hover:bg-rose-500/10"
            >
              <XCircle className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!selected) {
    return (
      <div className="rounded-xl border border-dashed border-white/20 p-6 text-center text-sm text-zinc-500" data-testid="zone-editor-detail-empty">
        <MapPinned className="h-8 w-8 mx-auto mb-2 opacity-50" />
        Draw a new zone with the map controls (top-right) or select an existing zone from the list.
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-white/10 bg-[#343A40] p-4" data-testid={`zone-editor-detail-${selected.id}`}>
      <h2 className="text-sm font-bold uppercase tracking-wider text-amber-300 mb-3 flex items-center gap-2">
        {selected.shape === "polygon" ? <Pentagon className="h-4 w-4" /> : <CircleIcon className="h-4 w-4" />}
        Edit · {selected.label}
      </h2>
      <div className="space-y-3">
        <div>
          <label className="block text-xs text-zinc-400 mb-1">Label</label>
          <Input
            data-testid="zone-edit-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="bg-black/40 border-white/20 text-white"
          />
        </div>
        <div>
          <label className="block text-xs text-zinc-400 mb-1">Heat hint</label>
          <select
            data-testid="zone-edit-heat"
            value={heatHint}
            onChange={(e) => setHeatHint(e.target.value)}
            className="w-full bg-black/40 border border-white/20 rounded-md px-3 py-1.5 text-sm text-white"
          >
            <option value="hot">Hot (peak demand)</option>
            <option value="warm">Warm</option>
            <option value="quiet">Quiet</option>
          </select>
        </div>
        {selected.shape === "circle" && (
          <div>
            <label className="block text-xs text-zinc-400 mb-1">Radius (miles)</label>
            <Input
              data-testid="zone-edit-radius"
              type="number" step="0.1" min="0.1"
              value={radius}
              onChange={(e) => setRadius(e.target.value)}
              className="bg-black/40 border-white/20 text-white"
            />
          </div>
        )}
        <div className="text-[11px] text-zinc-500 space-y-0.5">
          <div>Status: <span className="font-mono">{selected.status}</span></div>
          <div>Created: {fmt(selected.created_at)} · by {selected.created_by}</div>
          {selected.updated_at !== selected.created_at && (
            <div>Updated: {fmt(selected.updated_at)} · by {selected.updated_by}</div>
          )}
          {selected.published_at && (
            <div>Published: {fmt(selected.published_at)} · by {selected.published_by}</div>
          )}
        </div>
        <Button
          onClick={async () => {
            setBusy(true);
            try {
              const patch = { label: label.trim(), heat_hint: heatHint };
              if (selected.shape === "circle") patch.radius_mi = parseFloat(radius);
              await onSaveEdit(selected.id, patch);
            } finally { setBusy(false); }
          }}
          disabled={busy || !label || label.trim().length < 2}
          data-testid="zone-edit-save"
          className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold"
        >
          <Save className="h-4 w-4 mr-2" />Save Changes
        </Button>
      </div>
    </div>
  );
}

/* ─────────────────────────── Page shell ─────────────────────────── */
export default function DriverAppManagerPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [zones, setZones] = useState([]);
  const [analyticsByKey, setAnalyticsByKey] = useState({});
  const [analyticsCounts, setAnalyticsCounts] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [draftPayload, setDraftPayload] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [drawing, setDrawing] = useState(null);  // "circle" | "polygon" | null
  const fileInputRef = useRef(null);
  const mapRef = useRef(null);
  const drawApiRef = useRef(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await adminApi.get("/admin/driver-app-manager/zones");
      setZones(data?.zones || []);
      setDenied(false);
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
      else toast.error("Could not load zones");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const loadAnalytics = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/driver-app-manager/zones/analytics");
      const rows = data?.zones || [];
      const map = {};
      for (const r of rows) map[r.key] = r;
      setAnalyticsByKey(map);
      setAnalyticsCounts(data?.counts || null);
    } catch (_e) {
      // Silent — the base zones list still renders without analytics
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    loadAnalytics();
    const t = setInterval(loadAnalytics, 45000);
    return () => clearInterval(t);
  }, [loadAnalytics]);

  const selected = useMemo(() => zones.find((z) => z.id === selectedId) || null, [zones, selectedId]);

  const handleCreated = useCallback((payload) => {
    setSelectedId(null);
    setDraftPayload(payload);
    setDrawing(null);
  }, []);

  const startDrawCircle = useCallback(() => {
    setDrawing("circle");
    setDraftPayload(null);
    drawApiRef.current?.startCircle?.();
  }, []);
  const startDrawPolygon = useCallback(() => {
    setDrawing("polygon");
    setDraftPayload(null);
    drawApiRef.current?.startPolygon?.();
  }, []);
  const cancelDraw = useCallback(() => {
    drawApiRef.current?.cancel?.();
    setDrawing(null);
  }, []);

  const handleSaveNewDraft = useCallback(async (payload) => {
    try {
      const { data } = await adminApi.post("/admin/driver-app-manager/zones", payload);
      toast.success(`Zone "${data.label}" saved as draft`);
      setDraftPayload(null);
      setSelectedId(data.id);
      await load();
      loadAnalytics();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  }, [load, loadAnalytics]);

  const handleSaveEdit = useCallback(async (id, patch) => {
    try {
      await adminApi.patch(`/admin/driver-app-manager/zones/${id}`, patch);
      toast.success("Zone updated");
      await load();
      loadAnalytics();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  }, [load, loadAnalytics]);

  const handlePublish = useCallback(async (id) => {
    try {
      await adminApi.post(`/admin/driver-app-manager/zones/${id}/publish`);
      toast.success("Published — drivers will see it in the next 45s poll");
      await load();
      loadAnalytics();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    }
  }, [load, loadAnalytics]);

  const handleUnpublish = useCallback(async (id) => {
    try {
      await adminApi.post(`/admin/driver-app-manager/zones/${id}/unpublish`);
      toast.success("Unpublished — zone is back to draft");
      await load();
      loadAnalytics();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unpublish failed");
    }
  }, [load, loadAnalytics]);

  const handleDelete = useCallback(async (id) => {
    try {
      await adminApi.delete(`/admin/driver-app-manager/zones/${id}`);
      toast.success("Zone deleted");
      if (selectedId === id) setSelectedId(null);
      await load();
      loadAnalytics();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  }, [selectedId, load, loadAnalytics]);

  // v2.7.65+E130 · GeoJSON export — downloads .geojson via a temp anchor.
  const handleExport = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/driver-app-manager/zones/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/geo+json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `buller-taxi-zones-${new Date().toISOString().slice(0, 10)}.geojson`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data?.meta?.count || 0} zones as GeoJSON`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    }
  }, []);

  // v2.7.65+E130 · GeoJSON import — <input type=file> then POST.
  const handleImportFile = useCallback(async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const { data } = await adminApi.post("/admin/driver-app-manager/zones/import", parsed);
      const msg = `Imported ${data.imported} · Skipped ${data.skipped} · Errors ${data.errors}`;
      if (data.imported > 0) toast.success(msg);
      else toast.error(msg);
      await load();
      loadAnalytics();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail
        || (e instanceof SyntaxError ? "Invalid JSON in file" : "Import failed")
      );
    } finally {
      setImporting(false);
    }
  }, [load, loadAnalytics]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-zinc-300" data-testid="driver-app-manager-loading">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading Driver App Manager…
      </div>
    );
  }

  if (denied) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-zinc-950 text-zinc-200 p-6" data-testid="driver-app-manager-denied">
        <Lock className="h-12 w-12 text-rose-400 mb-3" />
        <h1 className="text-xl font-bold mb-1">Access Denied</h1>
        <p className="text-sm text-zinc-400 mb-4 max-w-md text-center">
          The Driver App Manager is restricted to super_user and general_partner
          accounts. Your role does not currently carry that grant.
        </p>
        <Button variant="outline" onClick={() => navigate("/admin")} data-testid="driver-app-manager-denied-back" className="gap-2">
          <ArrowLeft className="h-4 w-4" />Back to Admin Console
        </Button>
      </div>
    );
  }

  const draftCount = zones.filter((z) => z.status === "draft").length;
  const pubCount = zones.filter((z) => z.status === "published").length;

  return (
    <div
      data-testid="driver-app-manager-root"
      className="min-h-screen text-zinc-100 px-4 sm:px-6 lg:px-10 py-6"
      style={{ backgroundColor: "#212529" }}
    >
      {/* v2.7.65+E131 · CSS overrides to make drawing feedback visible on
          our dark theme. Guide-tooltip + vertex markers get bright amber
          styling with strong contrast rings so admins never lose sight of
          what they're drawing. */}
      <style>{`
        .leaflet-draw-tooltip {
          background: #FFD200 !important;
          color: #212529 !important;
          border: 2px solid #E0115F !important;
          font-weight: 700 !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.4) !important;
          font-size: 13px !important;
          padding: 6px 10px !important;
        }
        .leaflet-draw-tooltip:before { border-right-color: #FFD200 !important; }
        .bt-draw-vertex-marker,
        .leaflet-div-icon {
          background: #FFD200 !important;
          border: 3px solid #E0115F !important;
          border-radius: 50% !important;
          box-shadow: 0 0 0 3px rgba(255,210,0,0.4), 0 0 10px rgba(224,17,95,0.6) !important;
        }
        .leaflet-draw-guide-dash {
          background: #FFD200 !important;
          opacity: 1 !important;
        }
        .leaflet-editing-icon {
          background: #FFD200 !important;
          border: 3px solid #E0115F !important;
        }
      `}</style>

      {/* Header */}
      <div className="max-w-7xl mx-auto mb-5">
        <Button
          variant="ghost" size="sm"
          onClick={() => navigate("/admin")}
          className="mb-3 -ml-2 text-zinc-400 hover:text-white"
          data-testid="driver-app-manager-back"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />Back to Admin Console
        </Button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div
              className="rounded-lg p-2.5 flex-shrink-0"
              style={{ backgroundColor: "rgba(255,210,0,0.10)", border: "1px solid #FFD200" }}
            >
              <MapPinned className="h-7 w-7" style={{ color: "#FFD200" }} />
            </div>
            <div>
              <h1
                className="text-2xl sm:text-3xl font-extrabold uppercase tracking-wide"
                style={{ color: "#FFD200" }}
                data-testid="driver-app-manager-title"
              >
                Driver App Manager
              </h1>
              <p className="text-sm text-zinc-400 mt-1 max-w-3xl">
                Draw dispatch / turbo zones on the map. Circles or polygons.
                Zones start as drafts — click <span className="text-emerald-400 font-semibold">Publish</span> to
                push them into the driver Wait-Times feed.
              </p>
              <div className="mt-2 flex items-center gap-2 text-xs flex-wrap">
                <span
                  className="uppercase font-bold px-2 py-1 rounded-full border tracking-wider"
                  data-testid="driver-app-manager-count-draft"
                  style={{ color: STATUS_STYLES.draft.color, borderColor: STATUS_STYLES.draft.color, backgroundColor: STATUS_STYLES.draft.fill }}
                >{draftCount} draft</span>
                <span
                  className="uppercase font-bold px-2 py-1 rounded-full border tracking-wider"
                  data-testid="driver-app-manager-count-published"
                  style={{ color: STATUS_STYLES.published.color, borderColor: STATUS_STYLES.published.color, backgroundColor: STATUS_STYLES.published.fill }}
                >{pubCount} published</span>
                {analyticsCounts && (
                  <span
                    className="uppercase font-bold px-2 py-1 rounded-full border tracking-wider text-sky-300 border-sky-500 bg-sky-500/10"
                    data-testid="driver-app-manager-count-seeded"
                  >{analyticsCounts.seeded} seeded (live)</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline" size="sm"
                onClick={handleExport}
                data-testid="driver-app-manager-export"
                className="border-amber-500 text-amber-300 hover:bg-amber-500/10"
              >
                <Download className="h-4 w-4 mr-1" />Export GeoJSON
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".geojson,.json,application/geo+json,application/json"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleImportFile(f);
                  e.target.value = "";
                }}
                className="hidden"
                data-testid="driver-app-manager-import-input"
              />
              <Button
                variant="outline" size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                data-testid="driver-app-manager-import"
                className="border-amber-500 text-amber-300 hover:bg-amber-500/10"
              >
                {importing
                  ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  : <Upload className="h-4 w-4 mr-1" />}
                Import
              </Button>
              <Button
                variant="ghost" size="sm"
                onClick={() => navigate("/admin/app-management-portal")}
                data-testid="driver-app-manager-portal-link"
                className="text-zinc-300 hover:text-amber-300"
              >
                <ShieldCheck className="h-4 w-4 mr-1" />App Portal
              </Button>
            </div>
            <div className="text-xs text-zinc-500 hidden sm:flex items-center gap-2">
              <Plus className="h-4 w-4 text-amber-400" />
              Use the map toolbar (top-right) to draw new circles or polygons
            </div>
          </div>
        </div>
      </div>

      {/* Presets bar */}
      <div className="max-w-7xl mx-auto mb-4 relative">
        <PresetsBar onPresetActivated={() => { load(); loadAnalytics(); }} />
      </div>

      {/* Grid: map (2 cols) + list + detail (1 col) */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-3">
          {/* v2.7.65+E131 · Big labeled draw controls — replaces the tiny
              top-right toolbar icons that were being missed. */}
          <div
            className="flex items-center gap-2 flex-wrap rounded-xl border border-amber-500/30 bg-black/40 p-2"
            data-testid="draw-toolbar"
          >
            <Button
              onClick={drawing === "circle" ? cancelDraw : startDrawCircle}
              data-testid="draw-circle-btn"
              className={
                drawing === "circle"
                  ? "bg-rose-600 hover:bg-rose-500 text-white font-bold"
                  : "bg-amber-500 hover:bg-amber-400 text-black font-bold"
              }
            >
              {drawing === "circle" ? (
                <><XCircle className="h-4 w-4 mr-1" />Cancel Circle</>
              ) : (
                <><CircleIcon className="h-4 w-4 mr-1" />+ Draw Circle</>
              )}
            </Button>
            <Button
              onClick={drawing === "polygon" ? cancelDraw : startDrawPolygon}
              data-testid="draw-polygon-btn"
              className={
                drawing === "polygon"
                  ? "bg-rose-600 hover:bg-rose-500 text-white font-bold"
                  : "bg-amber-500 hover:bg-amber-400 text-black font-bold"
              }
            >
              {drawing === "polygon" ? (
                <><XCircle className="h-4 w-4 mr-1" />Cancel Polygon</>
              ) : (
                <><Pentagon className="h-4 w-4 mr-1" />+ Draw Polygon</>
              )}
            </Button>
            {drawing && (
              <div
                className="text-xs text-amber-200 flex items-center gap-1.5 pl-2 border-l border-amber-500/40"
                data-testid="draw-hint"
              >
                {drawing === "circle" ? (
                  <>Click & drag on the map to size the circle. Release to finish.</>
                ) : (
                  <>Click on the map to add vertices. Click the first vertex or press <kbd className="px-1 rounded bg-amber-500/20 border border-amber-500/40">Enter</kbd> to finish.</>
                )}
              </div>
            )}
          </div>
          <ZoneMap
            zones={zones}
            analyticsByKey={analyticsByKey}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onCreated={handleCreated}
            onEdited={handleSaveEdit}
            mapRef={mapRef}
            drawApiRef={drawApiRef}
          />
        </div>
        <div className="space-y-4">
          <ZoneList
            zones={zones}
            analyticsByKey={analyticsByKey}
            selectedId={selectedId}
            onSelect={(id) => { setSelectedId(id); setDraftPayload(null); }}
            onDelete={handleDelete}
            onPublish={handlePublish}
            onUnpublish={handleUnpublish}
            onRefresh={() => { load(); loadAnalytics(); }}
            loading={refreshing}
          />
          <ZoneDetailPanel
            selected={selected}
            draftPayload={draftPayload}
            onDiscardDraft={() => setDraftPayload(null)}
            onSaveDraft={handleSaveNewDraft}
            onSaveEdit={handleSaveEdit}
          />
        </div>
      </div>

      {/* v2.7.65+E131 · Zone Heatmap Snapshot — full-width panel below the main grid */}
      <div className="max-w-7xl mx-auto mt-4">
        <HeatmapPanel />
      </div>
    </div>
  );
}
